import React, { useMemo } from 'react'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts'
import { computeMultiplier, addDaysToDate, dateToKey } from '@/lib/ramp-math'
import { todayIso } from '@/lib/calendar-utils'

interface TimelineEvent {
  eventName: string
  startDate: string
  endDate: string
  effectiveMultiplier: number
  effectiveRampUpDays: number
  effectiveRampDownDays: number
  effectiveRampCurve: 'linear' | 'ease_in'
  enabled: boolean
}

interface EventTimelineChartProps {
  events: TimelineEvent[]
}

export default function EventTimelineChart({ events }: EventTimelineChartProps) {
  const data = useMemo(() => {
    const enabledEvents = events.filter((e) => e.enabled)
    if (enabledEvents.length === 0) return []

    const multiplierMap = new Map<string, { multiplier: number; eventName: string; zone: string }>()

    enabledEvents.forEach((evt) => {
      const start = new Date(evt.startDate + 'T00:00:00')
      const end = new Date(evt.endDate + 'T00:00:00')
      const peak = evt.effectiveMultiplier

      // Ramp-up — use addDaysToDate for Date objects
      for (let i = evt.effectiveRampUpDays; i >= 1; i--) {
        const d = addDaysToDate(start, -i)
        const key = dateToKey(d)
        const dayIndex = evt.effectiveRampUpDays - i + 1
        const progress = dayIndex / (evt.effectiveRampUpDays + 1)
        const mult = computeMultiplier(peak, progress, evt.effectiveRampCurve)
        const existing = multiplierMap.get(key)
        if (!existing || mult > existing.multiplier) {
          multiplierMap.set(key, { multiplier: mult, eventName: evt.eventName, zone: 'ramp-up' })
        }
      }

      // Peak
      const peakDays = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1
      for (let i = 0; i < peakDays; i++) {
        const d = addDaysToDate(start, i)
        const key = dateToKey(d)
        const existing = multiplierMap.get(key)
        if (!existing || peak > existing.multiplier) {
          multiplierMap.set(key, { multiplier: peak, eventName: evt.eventName, zone: 'peak' })
        }
      }

      // Ramp-down — use addDaysToDate for Date objects
      for (let i = 1; i <= evt.effectiveRampDownDays; i++) {
        const d = addDaysToDate(end, i)
        const key = dateToKey(d)
        const dayIndex = evt.effectiveRampDownDays - i + 1
        const progress = dayIndex / (evt.effectiveRampDownDays + 1)
        const mult = computeMultiplier(peak, progress, evt.effectiveRampCurve)
        const existing = multiplierMap.get(key)
        if (!existing || mult > existing.multiplier) {
          multiplierMap.set(key, { multiplier: mult, eventName: evt.eventName, zone: 'ramp-down' })
        }
      }
    })

    const sorted = [...multiplierMap.entries()].sort(([a], [b]) => a.localeCompare(b))

    // Insert 1.0x baseline points between event clusters for clean chart rendering
    const result: { date: string; label: string; multiplier: number; eventName: string; zone: string }[] = []
    let prevDateKey: string | null = null
    const fmtDate = (dt: Date) => dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })

    sorted.forEach(([dateKey, data]) => {
      const d = new Date(dateKey + 'T00:00:00')

      if (prevDateKey) {
        const prev = new Date(prevDateKey + 'T00:00:00')
        const daysBetween = Math.round((d.getTime() - prev.getTime()) / (1000 * 60 * 60 * 24))
        if (daysBetween > 1) {
          const padAfter = addDaysToDate(prev, 1)
          result.push({ date: dateToKey(padAfter), label: fmtDate(padAfter), multiplier: 1, eventName: '', zone: 'baseline' })
          const padBefore = addDaysToDate(d, -1)
          if (dateToKey(padBefore) !== dateToKey(padAfter)) {
            result.push({ date: dateToKey(padBefore), label: fmtDate(padBefore), multiplier: 1, eventName: '', zone: 'baseline' })
          }
        }
      } else {
        const padBefore = addDaysToDate(d, -1)
        result.push({ date: dateToKey(padBefore), label: fmtDate(padBefore), multiplier: 1, eventName: '', zone: 'baseline' })
      }

      result.push({
        date: dateKey,
        label: fmtDate(d),
        multiplier: Math.round(data.multiplier * 1000) / 1000,
        eventName: data.eventName,
        zone: data.zone,
      })
      prevDateKey = dateKey
    })

    if (prevDateKey) {
      const last = new Date(prevDateKey + 'T00:00:00')
      const padAfter = addDaysToDate(last, 1)
      result.push({ date: dateToKey(padAfter), label: fmtDate(padAfter), multiplier: 1, eventName: '', zone: 'baseline' })
    }

    return result
  }, [events])

  if (data.length === 0) return null

  const today = todayIso()

  return (
    <div className="rounded-xl border border-border/60 bg-white p-5 shadow-sm">
      <h3 className="text-sm font-semibold text-foreground mb-4">Event Timeline</h3>
      <ResponsiveContainer width="100%" height={180}>
        <AreaChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: -20 }}>
          <defs>
            <linearGradient id="timelineFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#0d9488" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#0d9488" stopOpacity={0.05} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 10, fill: '#64748b' }}
            tickLine={false}
            axisLine={{ stroke: '#e2e8f0' }}
            interval="preserveStartEnd"
          />
          <YAxis
            domain={[1, 'auto']}
            tick={{ fontSize: 10, fill: '#64748b' }}
            tickFormatter={(v: number) => `${v}x`}
            tickLine={false}
            axisLine={false}
          />
          <ReferenceLine y={1} stroke="#94a3b8" strokeDasharray="4 4" />
          {data.some((d) => d.date === today) && (
            <ReferenceLine
              x={data.find((d) => d.date === today)?.label}
              stroke="#0f172a"
              strokeDasharray="4 4"
              label={{ value: 'Today', fontSize: 9, fill: '#0f172a', position: 'top' }}
            />
          )}
          <Tooltip
            formatter={(value: number | undefined) => [`${value ?? 0}x (${(((value ?? 0) - 1) * 100).toFixed(1)}%)`, 'Multiplier']}
            labelFormatter={(label: any, payload: readonly any[]) => {
              if (payload?.[0]?.payload) {
                return `${label} - ${payload[0].payload.eventName} (${payload[0].payload.zone})`
              }
              return label
            }}
            contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #e2e8f0' }}
          />
          <Area
            type="monotone"
            dataKey="multiplier"
            stroke="#0d9488"
            strokeWidth={2}
            fill="url(#timelineFill)"
            dot={false}
            activeDot={{ r: 3, fill: '#0d9488' }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  )
}
